import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import CustomButton from '../../components/CustomButton';
import RowActionsMenu from '../../components/RowActionsMenu';
import {
    useDeleteTenantUserMutation,
    useGetAccountQuery,
    useGetAccountUsersQuery,
} from '../../api/account/accountApiSlice';
import { AccountUser, UserRole } from '../../api/account/accountApiTypes';
import {
    enqueueErrorSnackbar,
    enqueueSuccessSnackbar,
} from '../../action/snackbarCommands';
import UserFormDialog from './UserFormDialog';
import { errorMessageFrom } from '../../utils/errorMessage';

interface UsersSectionProps {
    /** When true (non-owner view) the add/remove actions are hidden — the list
     *  stays visible read-only, like the other account sections. */
    readOnly?: boolean;
}


/**
 * Account "Users" section: the team roster for the tenant. Shows every
 * `app_user` (first name, last name, phone, role); the owner can add a
 * colleague, pick their role, and remove a colleague. Read-only for non-owners.
 * A user can't remove themselves, and the last remaining user can't be removed.
 */
const UsersSection = ({ readOnly = false }: UsersSectionProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { data: users, isLoading } = useGetAccountUsersQuery();
    const { data: account } = useGetAccountQuery();
    const [deleteTenantUser, { isLoading: isDeleting }] = useDeleteTenantUserMutation();

    const [showAddDialog, setShowAddDialog] = useState(false);
    const [userToEdit, setUserToEdit] = useState<AccountUser | null>(null);
    const [userToDelete, setUserToDelete] = useState<AccountUser | null>(null);

    const currentUserId = account && 'user' in account ? account.user.id : undefined;

    const roleLabel = (role: UserRole): string => {
        switch (role) {
            case 'owner':
                return t('account.role_owner');
            case 'manager':
                return t('account.role_manager');
            case 'service':
                return t('account.role_service');
            default:
                return role;
        }
    };

    // A user is removable only when: the owner is editing (not read-only), it's
    // NOT the caller themselves, and removing it would still leave ≥ 1 user.
    const canRemove = (user: AccountUser): boolean =>
        !readOnly && user.id !== currentUserId && (users?.length ?? 0) > 1;

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;
        try {
            await deleteTenantUser(userToDelete.id).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: t('account.users_delete_success'), autoHideDuration: 3000 }));
            setUserToDelete(null);
        } catch (err) {
            dispatch(enqueueErrorSnackbar({ message: errorMessageFrom(err, t('account.users_delete_error'), t) }));
        }
    };

    return (
        <section className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}>
            <div className={'flex items-start justify-between gap-4 mb-1'}>
                <h2 className={'text-lg font-semibold text-foreground'}>
                    {t('account.users_section')}
                </h2>
                {!readOnly && (
                    <CustomButton
                        title={t('account.users_add_confirm')}
                        onClick={() => setShowAddDialog(true)}
                        isFilled={false}
                    />
                )}
            </div>
            <p className={'text-sm text-foreground/60 mb-5'}>
                {t('account.users_section_desc')}
            </p>

            {isLoading ? (
                <p className={'text-sm text-foreground/60'}>{t('common.loading')}</p>
            ) : !users || users.length === 0 ? (
                <p className={'text-sm text-foreground/60'}>{t('account.users_empty')}</p>
            ) : (
                <div className={'overflow-x-auto'}>
                    <table className={'w-full text-sm'}>
                        <thead>
                            <tr className={'border-b border-foreground/10'}>
                                <th className={'text-left py-2 text-foreground/60 font-medium'}>
                                    {t('account.first_name')}
                                </th>
                                <th className={'text-left py-2 text-foreground/60 font-medium'}>
                                    {t('account.last_name')}
                                </th>
                                <th className={'text-left py-2 text-foreground/60 font-medium'}>
                                    {t('account.phone')}
                                </th>
                                <th className={'text-left py-2 text-foreground/60 font-medium'}>
                                    {t('account.role')}
                                </th>
                                <th className={'w-20 py-2'} aria-hidden={'true'} />
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr key={user.id} className={'border-b border-foreground/5'}>
                                    <td className={'py-2 text-foreground'}>{user.firstName}</td>
                                    <td className={'py-2 text-foreground'}>{user.lastName}</td>
                                    <td className={'py-2 text-foreground/80'}>
                                        {user.phone || t('common.not_applicable')}
                                    </td>
                                    <td className={'py-2 text-foreground/80'}>{roleLabel(user.role)}</td>
                                    <td className={'py-2 text-right'}>
                                        {/* Both change something, so both sit in the row's
                                            menu, Entfernen last (design-decisions.md ›
                                            Zeilenaktionen in Tabellen). */}
                                        <RowActionsMenu
                                            rowName={`${user.firstName} ${user.lastName}`.trim() || t('common.missing')}
                                            items={[{
                                                id: 'edit',
                                                label: t('common.edit'),
                                                onClick: () => setUserToEdit(user),
                                                hidden: readOnly,
                                            }]}
                                            destructiveItems={[{
                                                id: 'remove',
                                                label: t('account.users_remove'),
                                                onClick: () => setUserToDelete(user),
                                                hidden: !canRemove(user),
                                            }]}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showAddDialog && <UserFormDialog onClose={() => setShowAddDialog(false)} />}

            {userToEdit && (
                <UserFormDialog user={userToEdit} onClose={() => setUserToEdit(null)} />
            )}

            {userToDelete && createPortal(
                <div className={'fixed inset-0 z-[1300] flex items-center justify-center p-4'}>
                    <div
                        className={'fixed inset-0 bg-black bg-opacity-50'}
                        onClick={() => setUserToDelete(null)}
                    />
                    <div
                        className={'relative z-10 bg-text-contrast rounded-lg shadow-xl w-full max-w-md'}
                        role={'dialog'}
                        aria-modal={'true'}
                    >
                        <div className={'p-6'}>
                            <h3 className={'text-lg font-semibold text-foreground mb-2'}>
                                {t('account.users_delete_title')}
                            </h3>
                            <p className={'text-sm text-foreground/70'}>
                                {t('account.users_delete_description', {
                                    name: `${userToDelete.firstName} ${userToDelete.lastName}`.trim(),
                                })}
                            </p>
                        </div>
                        <div className={'px-6 py-4 flex flex-row-reverse gap-3'}>
                            <CustomButton
                                title={t('account.users_delete_confirm')}
                                onClick={handleConfirmDelete}
                                loading={isDeleting}
                            />
                            <CustomButton
                                title={t('common.cancel')}
                                onClick={() => setUserToDelete(null)}
                                isFilled={false}
                            />
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </section>
    );
};

export default UsersSection;
